import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import PhotoUpload from '../components/PhotoUpload';
import LocationPicker from '../components/LocationPicker';
import HomeButton from '../components/HomeButton';
import { ArrowLeft, Save, Loader2, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrabajoForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    tecnico_asignado_id: '',
    tecnico_asignado_nombre: '',
    cliente_id: '',
    cliente_nombre: '',
    lugar_nombre: '',
    ciudad_nombre: '',
    direccion: '',
    estado: 'pendiente',
    materiales_usados: '',
    observaciones: '',
    horas_trabajadas: ''
  });
  const [fotos, setFotos] = useState([]);
  const [fotosBoletas, setFotosBoletas] = useState([]);
  const [fotosEquipos, setFotosEquipos] = useState([]);
  const [ubicacion, setUbicacion] = useState(null);
  const [trabajoCompletado, setTrabajoCompletado] = useState(false);
  const [tecnicos, setTecnicos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isEdit) {
      loadTrabajo();
    }
    if (user?.rol === 'administrador') {
      loadTecnicos();
      loadClientes();
    }
  }, [id]);

  const loadTecnicos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/tecnicos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTecnicos(response.data);
    } catch (err) {
      console.error('Error al cargar técnicos:', err);
    }
  };

  const loadClientes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/clientes-empresa`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientes(response.data);
    } catch (err) {
      console.error('Error al cargar clientes:', err);
    }
  };

  const loadTrabajo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/trabajos/${id}`);
      const trabajo = response.data;
      
      setFormData({
        titulo: trabajo.titulo || '',
        descripcion: trabajo.descripcion || '',
        tecnico_asignado_id: trabajo.tecnico_asignado || '',
        tecnico_asignado_nombre: trabajo.tecnico_asignado_nombre || '',
        cliente_id: trabajo.cliente_id || '',
        cliente_nombre: trabajo.cliente_nombre || '',
        lugar_nombre: trabajo.lugar_nombre || '',
        ciudad_nombre: trabajo.ciudad_nombre || '',
        direccion: trabajo.direccion || '',
        estado: trabajo.estado || 'pendiente',
        materiales_usados: trabajo.materiales_usados || '',
        observaciones: trabajo.observaciones || '',
        horas_trabajadas: trabajo.horas_trabajadas || ''
      });
      
      if (trabajo.fotos) setFotos(trabajo.fotos);
      if (trabajo.fotos_boletas) setFotosBoletas(trabajo.fotos_boletas);
      if (trabajo.fotos_equipos) setFotosEquipos(trabajo.fotos_equipos);
      if (trabajo.ubicacion_gps) setUbicacion(trabajo.ubicacion_gps);
      
      // Verificar si el trabajo está completado
      setTrabajoCompletado(trabajo.estado === 'completado');
    } catch (err) {
      setError('Error al cargar el trabajo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const dataToSend = {
        ...formData,
        fotos,
        fotos_boletas: fotosBoletas,
        fotos_equipos: fotosEquipos,
        ubicacion_gps: ubicacion,
        horas_trabajadas: formData.horas_trabajadas ? parseFloat(formData.horas_trabajadas) : null
      };

      // Auto-asignar al técnico actual si no hay técnico asignado
      if (!isEdit && user?.rol === 'tecnico' && !dataToSend.tecnico_asignado_id) {
        dataToSend.tecnico_asignado_id = user.id;
        dataToSend.tecnico_asignado_nombre = user.nombre_completo;
      }

      if (isEdit) {
        await axios.put(`${API}/trabajos/${id}`, dataToSend);
      } else {
        await axios.post(`${API}/trabajos`, dataToSend);
      }

      navigate(user?.rol === 'tecnico' ? '/tecnico' : '/admin/trabajos');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar el trabajo');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de eliminar este trabajo? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/trabajos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate('/admin/trabajos');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al eliminar el trabajo');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6" data-testid="trabajo-form">
      <div className="max-w-3xl mx-auto">
        <div className="flex gap-2 mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="back-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
          <HomeButton />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">
              {isEdit ? 'Editar Trabajo' : 'Nuevo Trabajo'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive" data-testid="form-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Título */}
              <div className="space-y-2">
                <Label htmlFor="titulo">Título del Trabajo *</Label>
                <Input
                  id="titulo"
                  name="titulo"
                  placeholder="Ej: Instalación de sistema eléctrico"
                  value={formData.titulo}
                  onChange={handleChange}
                  disabled={user?.rol === 'tecnico' && isEdit}
                  required
                  data-testid="titulo-input"
                />
                {user?.rol === 'tecnico' && isEdit && (
                  <p className="text-xs text-gray-500">El título no puede ser editado por técnicos</p>
                )}
              </div>

              {/* Descripción */}
              <div className="space-y-2">
                <Label htmlFor="descripcion">Descripción *</Label>
                <Textarea
                  id="descripcion"
                  name="descripcion"
                  placeholder="Describe el trabajo a realizar..."
                  value={formData.descripcion}
                  onChange={handleChange}
                  rows={4}
                  required
                  data-testid="descripcion-input"
                />
              </div>

              {/* Técnico y Cliente */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tecnico_asignado_id">Técnico Asignado</Label>
                  {user?.rol === 'administrador' ? (
                    <Select 
                      value={formData.tecnico_asignado_id} 
                      onValueChange={(value) => {
                        const tecnico = tecnicos.find(t => t.id === value);
                        setFormData({ 
                          ...formData, 
                          tecnico_asignado_id: value,
                          tecnico_asignado_nombre: tecnico?.nombre_completo || ''
                        });
                      }}
                    >
                      <SelectTrigger data-testid="tecnico-select">
                        <SelectValue placeholder="Seleccionar técnico" />
                      </SelectTrigger>
                      <SelectContent>
                        {tecnicos.map(tecnico => (
                          <SelectItem key={tecnico.id} value={tecnico.id}>
                            {tecnico.nombre_completo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={formData.tecnico_asignado_nombre}
                      disabled
                      data-testid="tecnico-input"
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cliente_id">Cliente</Label>
                  {user?.rol === 'administrador' ? (
                    <Select 
                      value={formData.cliente_id} 
                      onValueChange={(value) => {
                        const cliente = clientes.find(c => c.id === value);
                        setFormData({ 
                          ...formData, 
                          cliente_id: value,
                          cliente_nombre: cliente?.nombre || ''
                        });
                      }}
                    >
                      <SelectTrigger data-testid="cliente-select">
                        <SelectValue placeholder="Seleccionar cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientes.map(cliente => (
                          <SelectItem key={cliente.id} value={cliente.id}>
                            {cliente.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={formData.cliente_nombre}
                      disabled
                      data-testid="cliente-input"
                    />
                  )}
                </div>
              </div>

              {/* Lugar y Ciudad (solo lectura para técnicos) */}
              {(formData.lugar_nombre || formData.ciudad_nombre) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.lugar_nombre && (
                    <div className="space-y-2">
                      <Label htmlFor="lugar_nombre">Lugar</Label>
                      <Input
                        id="lugar_nombre"
                        value={formData.lugar_nombre}
                        disabled
                        className="bg-gray-50"
                      />
                    </div>
                  )}
                  
                  {formData.ciudad_nombre && (
                    <div className="space-y-2">
                      <Label htmlFor="ciudad_nombre">Ciudad</Label>
                      <Input
                        id="ciudad_nombre"
                        value={formData.ciudad_nombre}
                        disabled
                        className="bg-gray-50"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Dirección */}
              <div className="space-y-2">
                <Label htmlFor="direccion">Dirección</Label>
                <Input
                  id="direccion"
                  name="direccion"
                  placeholder="Dirección del trabajo"
                  value={formData.direccion}
                  onChange={handleChange}
                  data-testid="direccion-input"
                />
              </div>

              {/* Estado y Horas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="estado">Estado</Label>
                  <Select value={formData.estado} onValueChange={(value) => setFormData({ ...formData, estado: value })}>
                    <SelectTrigger data-testid="estado-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pendiente">Pendiente</SelectItem>
                      <SelectItem value="en_progreso">En Progreso</SelectItem>
                      <SelectItem value="completado">Completado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="horas_trabajadas">Horas Trabajadas</Label>
                  <Input
                    id="horas_trabajadas"
                    name="horas_trabajadas"
                    type="number"
                    step="0.5"
                    placeholder="Ej: 4.5"
                    value={formData.horas_trabajadas}
                    onChange={handleChange}
                    data-testid="horas-input"
                  />
                </div>
              </div>

              {/* Materiales */}
              <div className="space-y-2">
                <Label htmlFor="materiales_usados">Materiales Usados</Label>
                <Textarea
                  id="materiales_usados"
                  name="materiales_usados"
                  placeholder="Lista de materiales utilizados..."
                  value={formData.materiales_usados}
                  onChange={handleChange}
                  rows={3}
                  data-testid="materiales-input"
                />
              </div>

              {/* Observaciones */}
              <div className="space-y-2">
                <Label htmlFor="observaciones">Observaciones</Label>
                <Textarea
                  id="observaciones"
                  name="observaciones"
                  placeholder="Notas adicionales..."
                  value={formData.observaciones}
                  onChange={handleChange}
                  rows={3}
                  data-testid="observaciones-input"
                />
              </div>

              {/* Ubicación GPS */}
              <div className="space-y-2">
                <Label>Ubicación GPS</Label>
                <LocationPicker location={ubicacion} onLocationChange={setUbicacion} />
              </div>

              {/* Fotos */}
              <div className="space-y-2">
                <Label>Fotografías del Trabajo</Label>
                <PhotoUpload photos={fotos} onPhotosChange={setFotos} disabled={trabajoCompletado} />
              </div>

              {/* Fotos de Boletas - Solo visible para Admin y Técnico */}
              {user?.rol !== 'cliente' && (
                <div className="space-y-2">
                  <Label>Fotografías de Boletas (Máx. 10)</Label>
                  <PhotoUpload 
                    photos={fotosBoletas} 
                    onPhotosChange={setFotosBoletas} 
                    maxPhotos={10}
                    disabled={trabajoCompletado}
                  />
                </div>
              )}

              {/* Fotos de Equipos */}
              <div className="space-y-2">
                <Label>Fotografías de Equipos Usados (Máx. 10)</Label>
                <PhotoUpload 
                  photos={fotosEquipos} 
                  onPhotosChange={setFotosEquipos} 
                  maxPhotos={10}
                  disabled={trabajoCompletado}
                />
              </div>

              {/* Mensaje si está completado */}
              {trabajoCompletado && (
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertDescription className="text-yellow-800">
                    Este trabajo está completado y no se puede editar.
                  </AlertDescription>
                </Alert>
              )}

              {/* Botones */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                  data-testid="cancel-button"
                >
                  Cancelar
                </Button>
                
                {isEdit && user?.rol === 'administrador' && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
                    data-testid="delete-button"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Eliminando...
                      </>
                    ) : (
                      <>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Eliminar
                      </>
                    )}
                  </Button>
                )}
                
                <Button
                  type="submit"
                  disabled={saving || trabajoCompletado}
                  className="flex-1"
                  data-testid="save-button"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Guardar Trabajo
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TrabajoForm;